import React, { useEffect, useRef } from 'react'
import './GaugeChart.css'

interface GaugeChartProps {
    value: number
    label?: string
    goal?: number
    max?: number
    color?: string
    decimals?: number
    isLoading?: boolean
}

const GaugeChart: React.FC<GaugeChartProps> = ({
    value,
    label,
    goal = 80,
    max = 100,
    color = '#A9D3FF',
    decimals = 0,
    isLoading = false,
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const resizeTimeoutRef = useRef<NodeJS.Timeout>()

    const drawGauge = () => {
        const canvas = canvasRef.current
        if (!canvas) return

        const ctx = canvas.getContext('2d')
        if (!ctx) return

        const ratio = window.devicePixelRatio || 1
        const rect = canvas.getBoundingClientRect()

        canvas.width = rect.width * ratio
        canvas.height = rect.height * ratio
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
        ctx.clearRect(0, 0, rect.width, rect.height)

        const width = rect.width
        const height = rect.height

        const centerX = width / 2
        const centerY = height * 0.92
        const outerRadius = Math.min(centerX, height) * 0.9
        const innerRadius = outerRadius * 0.6
        const lineWidth = outerRadius - innerRadius
        const arcRadius = (outerRadius + innerRadius) / 2

        ctx.beginPath()
        ctx.arc(centerX, centerY, arcRadius, Math.PI, 2 * Math.PI, false)
        ctx.strokeStyle = '#f1f1f1'
        ctx.lineWidth = lineWidth
        ctx.lineCap = 'butt'
        ctx.stroke()

        if (value > 0 && !isLoading) {
            const percentage = Math.min(100, Math.max(0, (value / max) * 100))
            const filledAngle = Math.PI + (Math.PI * (percentage / 100))
            ctx.beginPath()
            ctx.arc(centerX, centerY, arcRadius, Math.PI, filledAngle, false)
            ctx.strokeStyle = color
            ctx.lineWidth = lineWidth
            ctx.lineCap = 'butt'
            ctx.stroke()

            const goalPercentage = Math.min(100, Math.max(0, (goal / max) * 100))
            const goalAngle = Math.PI + (Math.PI * (goalPercentage / 100))
            const startX = centerX + (arcRadius - lineWidth / 2) * Math.cos(goalAngle)
            const startY = centerY + (arcRadius - lineWidth / 2) * Math.sin(goalAngle)
            const endX = centerX + (arcRadius + lineWidth / 2) * Math.cos(goalAngle)
            const endY = centerY + (arcRadius + lineWidth / 2) * Math.sin(goalAngle)

            ctx.beginPath()
            ctx.moveTo(startX, startY)
            ctx.lineTo(endX, endY)
            ctx.strokeStyle = '#000'
            ctx.lineWidth = 2
            ctx.stroke()

            ctx.font = 'bold 11px Arial'
            ctx.fillStyle = '#000'

            const labelOffset = 35
            const labelX = endX + labelOffset * Math.cos(goalAngle)
            const labelY = endY + labelOffset * Math.sin(goalAngle) - 8

            if (goalAngle < Math.PI * 1.5) {
                ctx.textAlign = 'left'
            } else {
                ctx.textAlign = 'right'
            }

            ctx.fillText(`GOAL ${goal}%`, labelX, labelY)

            ctx.font = 'bold 20px Arial'
            ctx.fillStyle = '#4A4A4A'
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'

            const textYOffset = outerRadius * 0.25
            ctx.fillText(`${value.toFixed(decimals)}%`, centerX, centerY - textYOffset)
        }
    }

    useEffect(() => {
        drawGauge()

        const handleResize = () => {
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current)
            }
            resizeTimeoutRef.current = setTimeout(() => {
                drawGauge()
            }, 100)
        }

        window.addEventListener('resize', handleResize)

        return () => {
            window.removeEventListener('resize', handleResize)
            if (resizeTimeoutRef.current) {
                clearTimeout(resizeTimeoutRef.current)
            }
        }
    }, [value, goal, max, color, isLoading])

    return (
        <div className="gauge-box">
            {label && <div className="gauge-box-title">{label}</div>}
            <div className="gauge-chart-wrapper" style={{ position: 'relative' }}>
                <canvas
                    ref={canvasRef}
                    className="gauge-canvas"
                    style={{ width: '100%', height: '100%' }}
                />
                {value === 0 && !isLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#888',
                        fontSize: '12px',
                        fontFamily: 'Segoe UI, sans-serif',
                        textAlign: 'center',
                        padding: '0 20px',
                        pointerEvents: 'none',
                        marginTop: '60px'
                    }}>
                        <div style={{ fontWeight: '500' }}>No Data Available</div>
                    </div>
                )}
                {isLoading && (
                    <div className="chart-loading-overlay">
                        <div>Loading...</div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default GaugeChart
